import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Menu, X } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { originalSite, publicDestination, technologyKeys } from '@/data/navigation'

export function Header() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 50)
  const isHome = /^\/(it|en)\/?$/.test(location.pathname)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [group, setGroup] = useState<string | null>(null)
  const header = useRef<HTMLElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const updateScroll = () => setIsScrolled(window.scrollY > 50)
    updateScroll()
    window.addEventListener('scroll', updateScroll, { passive: true })
    return () => window.removeEventListener('scroll', updateScroll)
  }, [])
  useEffect(() => { setMobileOpen(false); setGroup(null) }, [location])
  useEffect(() => {
    const close = (event: PointerEvent) => { if (!header.current?.contains(event.target as Node)) { setGroup(null); setMobileOpen(false) } }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [])
  return <header className={`site-header ${isHome ? 'site-header--home' : ''} ${isScrolled || mobileOpen || group ? 'is-solid' : ''}`} ref={header} onKeyDown={event => {
    if (event.key === 'Escape') {
      if (group) { header.current?.querySelector<HTMLButtonElement>(`[aria-controls="nav-${group}"]`)?.focus(); setGroup(null) }
      else { setMobileOpen(false); toggle.current?.focus() }
    }
  }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) { setGroup(null); setMobileOpen(false) } }}>
    <div className="site-container header-row">
      <LocalizedLink to="home" className="brand-link" aria-label={t('shell.home')}><img src="/media/brand/m.png" width="41" height="40" alt={t('nav.siteName')} /></LocalizedLink>
      <button type="button" ref={toggle} className="menu-toggle" aria-expanded={mobileOpen} aria-controls="primary-navigation" aria-label={t(mobileOpen ? 'aria.closeMenu' : 'aria.openMenu')} onClick={() => { setMobileOpen(!mobileOpen); setGroup(null) }}>{mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}</button>
      <nav id="primary-navigation" className={`primary-navigation ${mobileOpen ? 'is-open' : ''}`} aria-label={t('aria.mainNavigation')}>
        <div className="nav-group"><button type="button" aria-expanded={group === 'technologies'} aria-controls="nav-technologies" onClick={() => setGroup(group === 'technologies' ? null : 'technologies')}>{t('nav.technologies')}<ChevronDown size={16} aria-hidden="true" /></button>
          <div id="nav-technologies" className="nav-dropdown" hidden={group !== 'technologies'}><LocalizedLink to={publicDestination('technologies')}>{t('shell.allTechnologies')}</LocalizedLink>{technologyKeys.map(key => <LocalizedLink key={key} to={publicDestination(key)}>{t(`models.${key}`)}</LocalizedLink>)}</div>
        </div>
        <div className="nav-group"><button type="button" aria-expanded={group === 'products'} aria-controls="nav-products" onClick={() => setGroup(group === 'products' ? null : 'products')}>{t('nav.products')}<ChevronDown size={16} aria-hidden="true" /></button>
          <div id="nav-products" className="nav-dropdown" hidden={group !== 'products'}>{(['professionalProducts', 'supplements'] as const).map(key => <LocalizedLink key={key} to={publicDestination(key)}>{t(`nav.${key}`)}</LocalizedLink>)}</div>
        </div>
        <LocalizedLink to={publicDestination('training')}>{t('nav.training')}</LocalizedLink>
        <LocalizedLink to={publicDestination('contacts')}>{t('nav.contacts')}</LocalizedLink>
        <LocalizedLink className="beauty-link" to={`${originalSite}/login/`}>{t('nav.beautyArea')}</LocalizedLink>
        <LanguageSelector />
      </nav>
    </div>
  </header>
}
